<template>
  <div class="event">
    <div class="event__wrapper">
      <div
        class="event__header-background"
        :style="{
          backgroundImage: `url(${event.header_image})`,
        }"
      ></div>
      <div class="event__content">
        <div class="event__details event__row">
          <div class="event__details-box event__column">
            <span class="event__details-box_data"
              >{{ event.formated_dates }} | {{ event.type }}</span
            >
            <h2 class="event__details-box_title">{{ event.title }}</h2>
            <span class="event__details-box_details">{{ event.description }}</span>
            <BaseButton
              v-if="event.button_text && event.button_url"
              class="button button--xs button--outline-white button--uppercase"
              :text="event.button_text"
              @click="goToLink(event.button_url)"
            />
          </div>

          <div class="event__info-box event__column event__column--one">
            <div class="event__info-box_section">
              <template v-if="event.type === 'Online'">
                <h3>
                  Event
                  {{ eventEnd ? "has ended:" : eventStart ? "ends in:" : "opens in:" }}
                </h3>
                <p class="event__info-box_time">
                  {{
                    eventEnd
                      ? $moment(event.end_date).format("MMMM Do")
                      : eventStart
                      ? $moment(event.end_date).format("MMMM Do")
                      : $moment(event.start_date).format("MMMM Do")
                  }}
                </p>
              </template>
              <template v-else>
                <h3>
                  Event
                  {{ eventEnd ? "has ended:" : eventStart ? "ends in:" : "opens in:" }}
                </h3>
                <p v-if="time !== '00d:00h:00m:00s'" class="event__info-box_time">
                  {{ time }}
                </p>
                <p v-else class="event__info-box_end-date">
                  Event ended on {{ $moment(event.end_date).format("MMMM Do, YYYY") }}
                </p>
              </template>
            </div>

            <div class="event__info-box_section">
              <h3>Event info:</h3>
              <div class="event__info-box_section_info">
                <p>{{ event.type }}</p>
                <p>{{ event.address }}</p>
              </div>
            </div>

            <div v-if="event.schedule" class="eventn__info-box_section">
              <h3>Gallery Dates/Hours:</h3>
              <div class="event__info-box_section_info">
                <div v-html="event.schedule" class="collect-editor collect-editor--white"></div>
              </div>
            </div>
            <div v-if="event.safety_protocol" class="event__info-box_section">
              <h3>Safety Protocol:</h3>
              <div
                v-html="event.safety_protocol_info"
                class="collect-editor collect-editor--white"
              ></div>
            </div>
            <div v-if="time !== '00d:00h:00m:00s' && dates.length" class="event__info-box_buttons">
              <BaseButton
                class="button button--full-width button--white"
                text="SCHEDULE YOUR VISIT"
                @click="toggleModal"
              />
            </div>
          </div>
        </div>

        <div v-if="exhibition.title" class="event__special-event-box">
          <span class="event__special-event-box_data"
            >{{ exhibition.dates }} | {{ exhibition.type }}</span
          >
          <span class="event__special-event-box_title">{{ exhibition.title }}</span>
          <BaseButton
            class="button button--xs button--white"
            text="VIEW ASSOCIATED EXHIBITION"
            @click="$router.push({ path: `/exhibitions/${exhibition.slug}` })"
          />
        </div>
        <div class="event__sponsors-box" v-if="event.sponsors.length > 0">
          <div class="event__sponsors-box_sponsors event__column">
            <h3 class="title">Sponsors</h3>
            <div class="container">
              <a
                v-for="(item, key) in event.sponsors"
                :key="`sponsor-${key}`"
                class="container__item sponsor-link"
                :class="{ 'sponsor-link--no-clickable': !item.url }"
                :href="item.url ? item.url : ''"
                target="_blank"
                @click.prevent="goToLink(item.url)"
              >
                <span class="container__item_box">
                  <img class="container__item_logo" :src="item.image" alt="Sponsor logo" />
                </span>
                <span>{{ item.caption }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <CreateReservation
      v-if="modal"
      :item="event"
      :modal="modal"
      :dates="dates"
      :timeslots="timeslots"
      :isExhibition="false"
      @hideModal="toggleModal"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import BaseButton from "@/components/buttons/BaseButton.vue";
import useSeoHead from "@/composables/useSeoHead";
import CreateReservation from "./components/CreateReservation.vue";

export default {
  components: { BaseButton, CreateReservation },

  data() {
    return {
      time: "00d:00h:00m:00s",
      timer: null,
      eventStart: false,
      eventEnd: false,
      modal: false,
    };
  },
  head() {
    const title = this.event?.title ?? "Event browngrotta arts";
    const description = this.event?.description ?? "Description Event browngrotta arts";
    const image = this.event?.header_image ?? "/img/icons/favicon-500x500.png";

    return useSeoHead({
      title,
      description: `${title} - ${description}`,
      image,
      routePath: this.$route.path,
    });
  },
  computed: {
    ...mapGetters("publicapi/events", {
      event: "getSpecificEvent",
      exhibition: "getAssociatedExhibition",
      dates: "getTimeslotDatesAvailability",
      timeslots: "getTimeslotAvailability",
    }),
  },

  async created() {
    await this.apiGetSpecificEvent(this.$route.params.slug);

    if (this.event.id) {
      await this.apiGetEventTimeslotDaysAvailability(this.event.id);
    }

    this.$emit("updateHead");
    const breadcrumbs = [
      {
        title: "Home",
        to: "/",
      },
      {
        title: "Events",
        to: "/events/events",
        clickable: true,
      },
      {
        title: `${this.event.title}`,
        clickable: false,
      },
    ];
    this.setBreadcrumbs(breadcrumbs);

    this.countDown();
    this.timer = setInterval(() => this.countDown(), 1000);

    this.setShareData({
      title: this.event.title,
      description: this.event.description,
      image: this.event.header_image,
    });
  },

  methods: {
    ...mapMutations(["setBreadcrumbs"]),
    ...mapMutations("publicapi/share", ["setShareData"]),
    ...mapActions("publicapi/events", [
      "apiGetSpecificEvent",
      "apiGetEventTimeslotDaysAvailability",
    ]),

    countDown() {
      const dateFormat = "YYYY-MM-DD HH:mm:ss";

      const currentTime = this.$moment().tz("America/New_York").format(dateFormat);
      const eventStart = this.$moment(this.event.start_date).format(dateFormat);
      const eventEnd = this.$moment(this.event.end_date).format(dateFormat);

      if (currentTime > eventEnd) {
        this.eventEnd = true;
      } else if (currentTime > eventStart) {
        this.eventStart = true;
        this.contTime(eventEnd, currentTime);
      } else if (currentTime < eventStart) {
        this.eventStart = false;
        this.contTime(eventStart, currentTime);
      }
    },
    contTime(start, end) {
      let seconds = this.$moment(start).diff(end, "seconds");
      if (seconds < 0) {
        seconds = 0;
      }
      let d = Math.floor(seconds / (3600 * 24));
      let h = Math.floor((seconds % (3600 * 24)) / 3600);
      let m = Math.floor((seconds % 3600) / 60);
      let s = Math.floor(seconds % 60);

      let dDisplay = d < 10 ? `0${d}` : d;
      let hDisplay = h < 10 ? `0${h}` : h;
      let mDisplay = m < 10 ? `0${m}` : m;
      let sDisplay = s < 10 ? `0${s}` : s;

      this.time = `${dDisplay}d:${hDisplay}h:${mDisplay}m:${sDisplay}s`;
    },

    goToLink(url) {
      if (url) {
        window.open(url, "_blank");
      }
    },
    toggleModal() {
      this.modal = !this.modal;
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  color: $white;
  font-size: 18px;
  line-height: 1.3;
  @media screen and (max-width: $xs) {
    font-size: 16px;
    line-height: 1.5;
  }
}

.event {
  display: flex;

  &__header {
    &-background {
      @include backgroundBlock();
    }
  }

  &__wrapper {
    @include fullScreenWrapper;
  }

  &__content {
    position: relative;
    width: 100%;
    margin-top: 40rem;
    padding: 0rem 13.7rem 7.4rem;
    z-index: 2;
    @media screen and (max-width: $md) {
      margin-top: 24.5rem;
      padding: 0rem 8rem 7.4rem;
    }
    @media screen and (max-width: $sm) {
      padding: 0rem 3rem 7.4rem;
    }
    @media screen and (max-width: $xs) {
      padding: 0rem 25px 11rem;
    }
    .event__row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 130px;
      @media screen and (max-width: $md) {
        flex-wrap: wrap;
        gap: 50px;
      }
      &--align-center {
        align-items: center;
      }
    }
    .event__column {
      width: 100%;
      &--one {
        max-width: 345px;
        padding: 18px 16px;
        border: 1px solid rgba(137, 137, 137, 1);
        backdrop-filter: blur(18.399999618530273px);
        border-radius: 12px;
        @media screen and (max-width: $md) {
          max-width: 100%;
        }

        .contact-item {
          font-size: 15px;
          margin-bottom: 4px;
        }
      }
    }
  }

  &__details-box {
    display: flex;
    flex-direction: column;

    &_data {
      font-size: 16px;
      line-height: 1.6;
      text-transform: uppercase;
      @media screen and (max-width: $xs) {
        font-size: 14px;
      }
    }

    &_title {
      font-size: 40px;
      line-height: 1.25;
      margin-bottom: 17px;
      @media screen and (max-width: $xs) {
        font-size: 3.5rem;
        line-height: 4.3rem;
      }
    }

    &_details {
      font-family: $fontFrutiger;
      font-weight: 100;
    }

    .button {
      margin-top: 22px;
      @media screen and (max-width: $xs) {
        margin-top: 35px;
      }
    }
  }

  &__info-box {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 2.5rem 0rem 0rem 14.2rem;
    @media screen and (max-width: $md) {
      width: 100%;
      padding: 9.5rem 0rem 0rem 0rem;
    }

    &_buttons {
      width: 100%;
      display: flex;
      gap: 1.5rem;
    }

    &_section {
      &:not(:last-child) {
        border-bottom: 0.6px solid rgba(137, 137, 137, 1);
        margin-bottom: 12px;
        padding-bottom: 12px;
      }
      h3 {
        font-size: 18px;
        line-height: 1.33;
        font-weight: 700;
        margin-bottom: 10px;
      }
      p {
        font-size: 16px;
        line-height: 1.33;
        font-weight: 400;
        margin-bottom: 8px;
      }
      &_info {
        display: flex;
        flex-direction: column;
      }
    }

    &_content {
      font-family: $fontFrutiger;
      font-weight: 100;
      margin-left: 10px;
    }

    &_info {
      display: flex;
      flex-direction: column;
      margin-bottom: 25px;
    }

    &_data {
      font-size: 27px;
      line-height: 1.85;
    }

    &_time {
      font-size: 27px;
      line-height: 1.85;
    }

    li {
      span {
        font-size: 16px;
        font-weight: 700;
        margin-left: -12px;
        @media not all and (min-resolution: 0.001dpcm) {
          @supports (-webkit-appearance: none) {
            margin-left: 0px;
          }
        }
        @supports (-moz-appearance: none) {
          margin-left: 0px;
        }
      }
    }
  }

  &__special-event-box {
    display: flex;
    align-items: center;
    flex-direction: column;
    margin-top: 17.6rem;
    width: 100%;

    &_data {
      font-size: 16px;
      line-height: 1.56;
      text-transform: uppercase;
    }

    &_title {
      font-size: 40px;
      line-height: 1.25;
      display: inline-block;
      text-align: center;
      @media screen and (max-width: $xs) {
        font-size: 35px;
        line-height: 1.2;
      }
    }

    .button {
      margin-top: 35px;
    }
  }
  &__sponsors-box {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    margin-top: 17.6rem;
    @media screen and (max-width: $lg) {
      justify-content: flex-start;
      margin-top: 15rem;
    }
    @media screen and (max-width: $md) {
      margin-top: 10rem;
    }
    @media screen and (max-width: $xs) {
      margin-top: 7.5rem;
    }

    .title {
      font-size: 4.65rem;
      line-height: 1.3;
      margin-bottom: 35px;
      @media screen and (max-width: $xs) {
        margin-bottom: 40px;
      }
    }

    &_sponsors {
      width: 100%;
      @media screen and (max-width: $md) {
        margin-bottom: 4rem;
      }

      .container {
        width: calc(100% + 30px);
        margin-left: -15px;
        display: flex;
        flex-wrap: wrap;

        &__item {
          max-width: 23.4rem;
          margin: 0 15px 30px;
          &_box {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 23.4rem;
            width: 23.4rem;
            border-radius: 17px;
            cursor: pointer;
            overflow: hidden;
            text-decoration: none;
            border: 1px solid $white;
            margin-bottom: 10px;
          }
        }
      }
      .sponsor-link {
        text-decoration: none;
        font-size: 18px;
        &--no-clickable {
          cursor: default;
        }
      }
    }
  }
}
</style>

